import { DataSource } from '@angular/cdk/table';
import { inject } from '@angular/core';
import { EmployeeBudgetDataAccessService } from '@flexyoo/employee/data-access';
import {
  EmployeeBudgetFilter,
  EmployeeBudgetSummary,
} from '@flexyoo/employee/models';
import { DataSourceSort, DataSourceState, Pager } from '@flexyoo/shared/models';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService } from '@flexyoo/auth';
import { Sort } from '@angular/material/sort';
import { LangService } from '@flexyoo/shared/services';

export class EmployeeBudgetListDataSource
  extends DataSource<EmployeeBudgetSummary>
  implements EmployeeBudgetListDataSource
{
  private readonly langService = inject(LangService);
  private auth = inject(AuthenticationService);
  private dataAccessService = inject(EmployeeBudgetDataAccessService);
  private refresh$ = new BehaviorSubject(null);
  private pager$ = new BehaviorSubject<Pager>({ pageIndex: 0, pageSize: 25 });
  private readonly sort$ = new BehaviorSubject<DataSourceSort>({
    SortByField: '',
    SortByDirection: '',
  });
  private filter$ = new BehaviorSubject<EmployeeBudgetFilter>({});
  private _state$ = new BehaviorSubject<DataSourceState>('loading');
  state$ = this._state$.asObservable();

  private result$ = combineLatest([
    this.auth.currentUserSubject,
    this.filter$,
    this.pager$,
    this.sort$,
    this.refresh$,
  ]).pipe(
    tap(() => {
      this._state$.next('loading');
    }),
    debounceTime(400),
    switchMap(([user, filter, pager, sort]) =>
      this.dataAccessService.getEmployeeBudgets(
        user.EmployeeId,
        filter,
        pager,
        sort
      )
    ),
    tap(() => {
      this._state$.next('loaded');
    }),
    shareReplay(1)
  );
  pagination$ = this.result$.pipe(map((result) => result.pagination));

  /** Connect function called by the table to retrieve one stream containing the data to render. */
  connect() {
    return this.result$.pipe(map((result) => result.items));
  }

  disconnect() {}

  nextFilter(filter: EmployeeBudgetFilter) {
    this.pager$.next({ pageIndex: 0, pageSize: this.pager$.value?.pageSize });
    this.filter$.next(filter);
  }

  nextPager(pager: Pager) {
    this.pager$.next(pager);
  }

  public setSort(sort: Sort): void {
    if (sort.direction === '') {
      this.sort$.next({ SortByDirection: '', SortByField: '' });
      return;
    }

    const sortValue: DataSourceSort = {
      SortByField: '',
      SortByDirection:
        sort.direction === 'asc' ? '0' : sort.direction === 'desc' ? '1' : '',
    };

    switch (sort.active) {
      case 'name': {
        switch (this.langService.lang) {
          case 'nl':
            sortValue.SortByField = '3';
            break;
          case 'fr':
            sortValue.SortByField = '2';
            break;
          case 'en':
            sortValue.SortByField = '4';
            break;
        }
        break;
      }
      case 'assignmentDate':
        sortValue.SortByField = '5';
        break;
      case 'type':
        sortValue.SortByField = '6';
        break;
      case 'grossAmount':
        sortValue.SortByField = '7';
        break;
      case 'budgetAmount':
        sortValue.SortByField = '8';
        break;
      case 'nsso':
        sortValue.SortByField = '9';
        break;
      case 'annualHolidayFee':
        sortValue.SortByField = '10';
        break;
    }

    this.sort$.next(sortValue);
  }
}
